import { useVirtualizer } from '@tanstack/react-virtual';
import { forwardRef, memo, type ReactNode, useImperativeHandle, useRef } from 'react';

export type DiscussionsVirtualizedListProps = {
  itemsCount: number;
  children: (props: { index: number }) => ReactNode;
};

export type DiscussionsVirtualizedListHandle = {
  scrollToBottom: () => void;
  getScrollElement: () => HTMLDivElement | null;
  scrollToIndex: (index: number) => void;
  isIndexVisible: (index: number) => boolean;
};

const _DiscussionsVirtualizedList = forwardRef<DiscussionsVirtualizedListHandle, DiscussionsVirtualizedListProps>(
  ({ itemsCount, children }: DiscussionsVirtualizedListProps, forwardedRef) => {
    const scrollElementRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
      count: itemsCount,
      estimateSize: () => 500,
      getScrollElement: () => scrollElementRef.current,
      overscan: 10,
      initialOffset: itemsCount * 500, // this sets the initial scroll position to the bottom, 500 is an arbitrary number, higher than default size
    });

    useImperativeHandle(forwardedRef, () => ({
      getScrollElement: () => scrollElementRef.current,
      scrollToBottom: () => virtualizer.scrollToIndex(itemsCount - 1),
      scrollToIndex: (index: number) => virtualizer.scrollToIndex(index, { align: 'center' }),
      isIndexVisible: (index: number) => {
        const virtualItems = virtualizer.getVirtualItems();
        return virtualItems.some((item) => item.index === index);
      },
    }));

    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div
        data-testid="CLIP_FACES_LIST_SCROLL"
        ref={scrollElementRef}
        className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent flex w-full flex-1 overflow-y-auto"
      >
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          <div
            className="absolute top-0 left-0 flex w-full flex-col"
            style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}
          >
            {virtualItems.map((virtualRow) => (
              <div key={virtualRow.key} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                {children({ index: virtualRow.index })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

_DiscussionsVirtualizedList.displayName = 'DiscussionsVirtualizedList';

export const DiscussionsVirtualizedList = memo(_DiscussionsVirtualizedList);

DiscussionsVirtualizedList.displayName = 'DiscussionsVirtualizedList';
